/**
 * This module provides utility functions that
 * determine the default audio and subtitle tracks
 * to be used.
 */

import { StateMapper } from 'easy-peasy';
import { StoreState } from '../store/store';
import { parseLanguage } from './parse-language';
import Player from 'video.js/dist/types/player';

/**
 * Get the audio tracks from the player.
 */
export function getAudioTracks(player?: Player | null): AudioTrack[] {
  if (!player) return [];
  const audioTrackList = player.audioTracks();
  return Array.from(audioTrackList as any) as AudioTrack[];
}

/**
 * Get the text tracks from the player.
 */
export function getTextTracks(player?: Player | null): TextTrackList {
  if (!player) return new TextTrackList();
  return player.textTracks() as unknown as TextTrackList;
}

/**
 * Determines the default audio track to be used.
 */
export function getDefaultAudioTrack(audioTracks: AudioTrack[], storeState: StateMapper<StoreState>) {
  if (audioTracks.length === 1) {
    return audioTracks[0];
  }

  return audioTracks.find((t: AudioTrack) => {
    /**
     * If audioTrack already selected, use the same.
     **/
    if (storeState.player.audioTrack) {
      return storeState.player.audioTrack.label === t.label;
    }

    /**
     * If language was previously selected by user, use the track with corresponding language.
     **/
    const { settings } = storeState;

    if (settings.user?.audio && audioTracks.some((t) => t.label === settings.user?.audio)) {
      return t.label === settings.user.audio;
    }

    /**
     * If language was specified by consumer, use the track with corresponding language.
     **/
    const configLanguage = parseLanguage(storeState.config.language);

    if (configLanguage && audioTracks.some((t) => t.language === configLanguage)) {
      return t.language === configLanguage;
    }

    /**
     * Otherwise, return the 1st track.
     **/
    return true;
  });
}

/**
 * Determines the default subtitle track to be used.
 */
export function getDefaultSubtitleTrack(storeState: StateMapper<StoreState>) {
  const { settings } = storeState;
  const { activeSubtitle, webVttSubtitles } = storeState.subtitles;

  /**
   * If subtitle already selected, use the same.
   **/
  if (activeSubtitle) {
    return null;
  }

  /**
   * If user's subtitles setting is persisted, use the persisted setting.
   **/
  const defaultSubtitle = [...webVttSubtitles].find((subtitle) => {
    return subtitle.label === settings.user?.subtitle;
  });

  return defaultSubtitle;
}
